import {
  signInAnonymously,
  signInWithCustomToken,
  onAuthStateChanged,
  signOut as fbSignOut,
} from "firebase/auth";
import { PROVIDERS } from "./providers/index.js";

// 네이티브 링크가 "이 신원은 이미 다른 계정 것" 이라고 알려주는 코드들.
// 이때만 로그인 → 회수 경로로 넘어간다.
const CONFLICT = new Set([
  "auth/email-already-in-use",
  "auth/credential-already-in-use",
  "auth/account-exists-with-different-credential",
]);

/**
 * 앱이 인증에 대해 아는 유일한 창구.
 *
 * @param auth          Firebase Auth 인스턴스
 * @param callFunction  (name, data) => Promise<any>  Functions 호출
 */
export function createAuthGateway({ auth, callFunction }) {
  const listProviders = () =>
    Object.values(PROVIDERS).map(({ id, label, kind }) => ({ id, label, kind }));

  const isAnonymous = () => auth.currentUser?.isAnonymous ?? true;

  /**
   * 앱 부팅 시 1회. 가입 마찰 없이 바로 토론을 시작하게 한다.
   *
   * ★ **복원을 먼저 기다린다.** `auth.currentUser` 는 새로고침 직후 항상 null 이다 —
   * 저장된 세션을 읽어 오는 게 비동기라서 그렇다. 기다리지 않고 읽으면 로그인한
   * 사용자를 못 보고 **새 익명 계정을 만들어 덮어쓴다.** 그러면 새로고침 한 번에
   * 로그인이 날아가고, 「내 기록」이 비고, 익명 uid 가 하나씩 쌓인다.
   */
  async function ensureSignedIn() {
    await authReady();
    if (auth.currentUser) return auth.currentUser;
    const { user } = await signInAnonymously(auth);
    return user;
  }

  /** SDK 가 authStateReady 를 주면 그걸 쓰고, 아니면 첫 상태 변화를 한 번 기다린다. */
  const authReady = () =>
    typeof auth.authStateReady === "function"
      ? auth.authStateReady()
      : new Promise((resolve) => {
          const stop = onAuthStateChanged(auth, () => { stop(); resolve(); });
        });

  async function upgrade(providerId, opts = {}) {
    const provider = PROVIDERS[providerId];
    if (!provider) throw new Error(`지원하지 않는 로그인 수단입니다: ${providerId}`);

    const user = auth.currentUser;
    if (!user) throw new Error("먼저 ensureSignedIn() 을 호출하세요.");

    // ★ 순서 함정 — 익명 ID 토큰은 지금 확보해야 한다.
    // 아래 signIn 이 성공하는 순간 익명 세션이 교체되어 증명할 방법이 사라진다.
    const anonIdToken = user.isAnonymous ? await user.getIdToken() : null;

    if (provider.kind === "exchange") {
      const credential = await provider.credential(opts);
      const { token, merged, movedDebates } = await callFunction("exchangeIdentityToken", {
        provider: providerId,
        credential,
      });
      const { user: next } = await signInWithCustomToken(auth, token);
      return { uid: next.uid, merged: !!merged, movedDebates: movedDebates ?? 0 };
    }

    // native — 링크가 되면 uid 가 그대로 유지된다. 서버를 부를 일이 없다.
    try {
      const { user: linked } = await provider.link(user, opts);
      return { uid: linked.uid, merged: false, movedDebates: 0 };
    } catch (e) {
      if (!CONFLICT.has(e.code)) throw e;

      // 이미 쓰던 계정이다. 그쪽으로 로그인한 뒤 익명 세션의 토론을 회수한다.
      const { user: existing } = await provider.signIn(auth, opts);
      if (!anonIdToken) return { uid: existing.uid, merged: false, movedDebates: 0 };

      const { merged, movedDebates } = await callFunction("claimAnonymousDebates", { anonIdToken });
      return { uid: existing.uid, merged: !!merged, movedDebates: movedDebates ?? 0 };
    }
  }

  /**
   * 로그아웃하고 곧바로 새 익명 세션을 연다.
   *
   * 세션 없이 두면 앱이 아무것도 못 한다 — 서버가 전부 uid 를 요구한다.
   * 그래서 "로그아웃" 은 정확히는 "익명으로 돌아가기" 다.
   *
   * 새 익명 uid 이므로 이전 기록은 이 브라우저에서 안 보인다.
   * 화면이 그걸 먼저 말해 줘야 한다.
   */
  async function signOut() {
    await fbSignOut(auth);
    const { user } = await signInAnonymously(auth);
    return user;
  }

  /**
   * 비밀번호 재설정. 제공자가 지원할 때만 쓸 수 있다 —
   * 커스텀 토큰 경로(구글)에는 비밀번호라는 것이 없다.
   */
  async function resetPassword(providerId, opts = {}) {
    const provider = PROVIDERS[providerId];
    if (!provider?.reset) throw new Error(`${providerId} 는 비밀번호 재설정이 없습니다.`);
    return provider.reset(auth, opts);
  }

  const onChange = (cb) => onAuthStateChanged(auth, cb);

  return { ensureSignedIn, upgrade, signOut, resetPassword, listProviders, isAnonymous, onChange };
}
